import { useState, useCallback } from "react";
import useStoreLinks from "../../../common/features/store/useStoreLinks.ts";
import EmptyAssetCard from "../../../common/features/asset/EmptyAssetCard.tsx";
import DefaultCoverBadge from "../../../common/features/asset/DefaultCoverBadge.tsx";
import AssetTopRow from "../../../common/features/asset/AssetTopRow.tsx";
import BaseLinkOrButton from "../../../common/core-ui/BaseLinkOrButton.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import MiscellaneousTag from "../../../common/icons/MiscellaneousTag.tsx";
import useFavorites from "../actions/favorites/useFavorites.tsx";
import NthEdition from "../../../common/features/asset/NthEdition.tsx";
import styles from "./ExpiredAsset.module.scss";
import TruncatedDescription from "../../../common/features/asset/TruncatedDescription.tsx";
import { clsx } from "clsx";
import ListAssetCardContainer from "../../../common/features/asset/ListAssetCardContainer.tsx";
import { ManageAssetWithLicense } from "../../../common/api/decimal/common-types/manageAssets.ts";
import useAccessibilityClaimsLink from "../common/accessibility-claims/useAccessibilityClaimsLink.tsx";
import { PanelType } from "../common/asset-panel/types.ts";
import AssetPanel from "../common/asset-panel/AssetPanel.tsx";

function GetAccessButton({
  asset,
  className,
}: {
  asset: ManageAssetWithLicense;
  className: string;
}) {
  const { isStoreAsset, makeRenewalUrl } = useStoreLinks();
  const intl = useIntl();

  if (isStoreAsset(asset.license)) {
    return (
      <BaseLinkOrButton
        href={makeRenewalUrl(asset.manageAsset.vbid, {
          utm_medium: "bookshelf-home-expired",
          utm_campaign: asset.manageAsset.vbid,
        })}
        className={clsx("vst-button-pill", styles.getAccessButton, className)}
        aria-label={intl.formatMessage(
          {
            defaultMessage: "Get Access to {title}",
          },
          { title: asset.manageAsset.title }
        )}
      >
        <MiscellaneousTag />
        <FormattedMessage defaultMessage="Get Access" />
      </BaseLinkOrButton>
    );
  }
}

export default function ExpiredAsset({
  asset,
}: {
  asset: ManageAssetWithLicense;
}) {
  const { favoritesMenuLink } = useFavorites(asset.manageAsset, asset.favorite);
  const [openPanelInfo, setOpenPanelInfo] = useState<{
    panel: string;
    time: number;
  }>();

  const setOpenAccessibilityPanel = useCallback(() => {
    setOpenPanelInfo({ panel: PanelType.Accessibility, time: Date.now() });
  }, []);

  const a11yClaimsMenuLink = useAccessibilityClaimsLink(
    setOpenAccessibilityPanel
  );
  return (
    <ListAssetCardContainer tagName="li" className={styles.wholeCard}>
      <EmptyAssetCard
        tagName="div"
        vbid={asset.manageAsset.vbid}
        type="horizontal"
        imageChildren={<DefaultCoverBadge license={asset.license} />}
        contentAreaClassName={styles.contentContainer}
        className={styles.baseCard}
      >
        <AssetTopRow
          asset={asset.manageAsset}
          license={asset.license}
          links={[a11yClaimsMenuLink, favoritesMenuLink]}
        />
        <div className={styles.columns}>
          <div>
            <h2 className={styles.title}>{asset.manageAsset.title}</h2>
            <NthEdition asset={asset.manageAsset} className={styles.edition} />
            <div className={styles.secondary}>
              <div>{asset.manageAsset.author}</div>
              <div className={styles.publisher}>
                {asset.manageAsset.publisher}
              </div>
            </div>
          </div>
          <GetAccessButton asset={asset} className={styles.bigOnly} />
        </div>
        <TruncatedDescription
          asset={asset.manageAsset}
          className={styles.description}
        />
        {openPanelInfo?.panel === PanelType.Accessibility && (
          <AssetPanel
            asset={asset.manageAsset}
            onRequestClose={() => setOpenPanelInfo(undefined)}
            key={openPanelInfo.time}
            startTab={PanelType.Accessibility}
          />
        )}
      </EmptyAssetCard>
      <GetAccessButton asset={asset} className={styles.smallOnly} />
    </ListAssetCardContainer>
  );
}
